import { useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api-client';
import { toast } from 'sonner';
import { ApiError } from '../errors';
import { refreshMyPicksCache } from './myPicksCache';

export type ReserveContestResponse = {
  id: string;
  status: string;
  reservedAt: string;
};

// The api-client response interceptor unwraps response.data, so the resolved value is
// the payload itself, not an AxiosResponse. Axios's second generic (R) is exactly the
// resolved type, so we set it rather than casting.
export const reserveContest = (pickemId: string) =>
  apiClient.post<ReserveContestResponse, ReserveContestResponse>(
    `/api/pickems/${pickemId}/reserve`
  );

/**
 * The server already has an entry (reservation or submitted picks) for this user.
 * Defined once here so the mutation and its callers agree on what a 409 means.
 */
export const isAlreadyEnteredError = (error: unknown): boolean =>
  error instanceof ApiError && error.statusCode === 409;

/**
 * Reserve a seat for a Squares contest — a real, zero-picks entry (free contests
 * only). Posts to the shared Pick'em reserve endpoint (DKPLAT-528); Squares has no
 * dedicated reserve endpoint of its own (DKPLAT-529). Mirrors
 * useSubmitSelectionMutation's cache-invalidation pattern, plus the lobby-list query
 * key so a reserved contest's CTA updates immediately on any contest list.
 *
 * `showToast` is for surfaces with no confirmation UI of their own (the lobby card).
 * PlayView leaves it off and shows SeatReservedModal instead, so the user never gets
 * both at once.
 */
export const useReserveContestMutation = ({ showToast = false }: { showToast?: boolean } = {}) => {
  const queryClient = useQueryClient();

  const refreshEntryState = async () => {
    // The ['squares'] prefix covers the lobby-list status variants plus
    // ['squares','my-picks'] and ['squares','my-selection']; ['square'] is the
    // contest-detail key, which carries has_reservation.
    queryClient.invalidateQueries({ queryKey: ['squares'] });
    queryClient.invalidateQueries({ queryKey: ['square'] });
    // Refetch my-picks so the reserved contest shows immediately (mirrors submit).
    await refreshMyPicksCache(queryClient);
  };

  return useMutation({
    mutationFn: (pickemId: string) => reserveContest(pickemId),
    onSuccess: async () => {
      await refreshEntryState();

      if (showToast) {
        toast.success("You're in! Come back to pick your squares once they're posted.");
      }
    },
    onError: (error: ApiError) => {
      // api-client always rejects with an ApiError (see api/errors.ts), which carries
      // `statusCode` rather than the raw axios `response`.
      if (isAlreadyEnteredError(error)) {
        toast.error('You have already entered this contest.');
        // The server has an entry we don't know about, so refresh rather than leaving
        // the CTA offering an action that can only fail again. Deliberately not
        // awaited: the mutation stays pending until onError settles, which would hold
        // the confirm modal on its "RESERVING…" state through the whole refetch.
        void refreshEntryState();
        return;
      }
      toast.error(error.message || 'Failed to reserve your seat');
    },
  });
};
